/**
 * @file ConfirmDialog.js
 * @version 1.3.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-11-17
 * @modified 2026-10-15
 *
 * 1.3.0 (2026-10-15): `confirmDisabled` (+ `confirmDisabledReason`, its
 * tooltip) greys Confirm until the body has been answered — the credit
 * note's stock question (CreditStockDialog) needs every line chosen first.
 *
 * 1.2.0 (2026-10-14): an optional SECOND choice beside Confirm —
 * `secondaryText` + `onSecondary` (+ `secondaryIcon`) — for a question with
 * two answers besides Cancel (Add Bill's "PO already billed"). `confirmIcon`
 * names Confirm's icon (default `check`). Without them nothing changes.
 * Each round button's label rides as its tooltip; say in the message which
 * icon is which choice.
 */

import React from 'react';
import { createPortal } from 'react-dom';
import './ConfirmDialog.css';
import { useSlideOutButton } from './SlideOutButton';

function ConfirmDialog({
  isOpen,
  title = 'Excel Agri System',
  message,
  onConfirm,
  onCancel,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'warning', // warning, info, error
  // 'split': cancel shrinks to half size at the bottom LEFT, confirm stays full
  // size at the bottom right — for a choice where confirming is the expected
  // answer and cancelling is the exception.
  actionsLayout = 'default',
  // Extra class on the dialog box, for a body wider than the default 500px.
  className = '',
  // A second answer besides Confirm (optional): shown between Cancel and Confirm.
  secondaryText = null,
  onSecondary = null,
  secondaryIcon = 'alt_route',
  // Confirm's icon.
  confirmIcon = 'check',
  // Confirm greyed until the body is answered, with why.
  confirmDisabled = false,
  confirmDisabledReason = null
}) {
  useSlideOutButton([isOpen]);

  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case 'error':
        return 'error';
      case 'info':
        return 'info';
      case 'warning':
      default:
        return 'warning';
    }
  };

  const getIconColor = () => {
    switch (type) {
      case 'error':
        return 'var(--color-error)';
      case 'info':
        return 'var(--color-info)';
      case 'warning':
      default:
        return 'var(--color-warning)';
    }
  };

  return createPortal(
    <div className="confirm-dialog-overlay">
      <div className={`confirm-dialog${className ? ` ${className}` : ''}`}>
        <div className="confirm-dialog-header">
          <span
            className="material-symbols-rounded"
            style={{ color: getIconColor(), fontSize: '24px' }}
          >
            {getIcon()}
          </span>
          <h3>{title}</h3>
        </div>
        <div className="confirm-dialog-body">
          {typeof message === 'string' ? <p>{message}</p> : message}
        </div>
        <div className={`confirm-dialog-actions${actionsLayout === 'split' ? ' is-split' : ''}`}>
          {/* The labels are hidden on a round button, so they ride along as the
              tooltip and the accessible name. */}
          <button
            className={`btn-round btn-danger${actionsLayout === 'split' ? ' is-small' : ''}`}
            onClick={onCancel}
            title={cancelText}
            aria-label={cancelText}
          >
            <div className="icon-wrapper">
              <span className="material-symbols-rounded">close</span>
            </div>
            <span className="btn-label">{cancelText}</span>
          </button>
          {secondaryText && onSecondary && (
            <button
              className="btn-round btn-secondary-choice"
              onClick={onSecondary}
              title={secondaryText}
              aria-label={secondaryText}
            >
              <div className="icon-wrapper">
                <span className="material-symbols-rounded">{secondaryIcon}</span>
              </div>
              <span className="btn-label">{secondaryText}</span>
            </button>
          )}
          <button
            className={`btn-round btn-success${confirmDisabled ? ' btn-disabled' : ''}`}
            onClick={confirmDisabled ? undefined : onConfirm}
            disabled={confirmDisabled}
            title={confirmDisabled && confirmDisabledReason ? confirmDisabledReason : confirmText}
            aria-label={confirmText}
          >
            <div className="icon-wrapper">
              <span style={{ color: 'var(--color-success)' }} className="material-symbols-rounded">{confirmIcon}</span>
            </div>
            <span className="btn-label">{confirmText}</span>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ConfirmDialog;
